<script>
import { ref, provide, useTemplateRef, computed as vueComputed } from 'vue';
import { useElementSize } from '@vueuse/core';
// composable
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ResizableEditorWrapper from './ResizableEditorWrapper.vue';
import ReferralBubble from 'dashboard/components-next/Conversation/ReferralBubble.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import {
  getTypingUsersText,
  groupTypingUsersByType,
} from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants, {
  META_RESTRICTION_STATUS_URL,
} from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  components: {
    MessageList,
    ReplyBox,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    ForwardMessageModal,
    NextButton,
    ResizableEditorWrapper,
    ReferralBubble,
  },
  mixins: [inboxMixin],
  provide() {
    return {
      isWahaConversation: vueComputed(() => this.isWahaConversation),
    };
  },
  setup() {
    const conversationPanelRef = ref(null);
    const resizableEditorWrapperRef = ref(null);
    const messagesViewRef = useTemplateRef('messagesViewRef');
    const topBannerRef = useTemplateRef('topBannerRef');
    const { height: containerHeight } = useElementSize(messagesViewRef);
    const { height: topBannerHeight } = useElementSize(topBannerRef);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      resizableEditorWrapperRef,
      messagesViewRef,
      topBannerRef,
      containerHeight,
      topBannerHeight,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      isSelectionMode: false,
      selectedMessages: [],
      isForwardModalOpen: false,
      pendingScrollMessageId: null,
      highlightTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
      isMetaMessageSendingDisabled: 'globalConfig/isMetaMessageSendingDisabled',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    isWahaConversation() {
      if (this.inbox?.inbox_identifier?.startsWith('waha_')) return true;
      const senderChatId =
        this.currentChat?.meta?.sender?.custom_attributes
          ?.waha_whatsapp_chat_id || '';
      return !!senderChatId;
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    typingGroups() {
      return groupTypingUsersByType(this.typingUsersList);
    },
    contactTypingText() {
      const { contacts } = this.typingGroups;
      if (!contacts.length) return '';
      const [i18nKey, params] = getTypingUsersText(contacts);
      return this.$t(i18nKey, params);
    },
    agentTypingText() {
      const { agents } = this.typingGroups;
      if (!agents.length) return '';
      const [i18nKey, params] = getTypingUsersText(agents);
      return this.$t(i18nKey, params);
    },
    isContactRecording() {
      return this.typingGroups.contacts.some(u => u.is_recording);
    },
    contactTypingIcon() {
      return this.isContactRecording ? '\uD83C\uDF99\uFE0F' : '\uD83D\uDCAC';
    },
    agentTypingIcon() {
      return '\uD83E\uDDD1\u200D\uD83D\uDCBC';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    referralData() {
      return this.currentChat?.additional_attributes?.referral || null;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },
    isInstagramRestrictionBannerVisible() {
      return this.isMetaMessageSendingDisabled && this.isAnInstagramChannel;
    },
    instagramRestrictionStatusUrl() {
      return META_RESTRICTION_STATUS_URL;
    },
    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count = this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.hasUserScrolled = false;
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.resetReplyEditorHeight();
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
    // Listen for selection mode toggle
    emitter.on('TOGGLE_MESSAGE_SELECTION_MODE', this.toggleSelectionMode);
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
    }
  },

  methods: {
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '', highlight = false } = {}) {
      if (!messageId) {
        this.$nextTick(() => {
          this.hasUserScrolled = false;
          this.scrollToBottom();
        });
        this.makeMessagesRead();
        return;
      }
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          if (highlight) {
            this.highlightMessage(messageElement);
          }
        } else {
          this.pendingScrollMessageId = messageId;
          this.loadMessageFromServer(messageId);
        }
      });
      this.makeMessagesRead();
    },
    highlightMessage(messageElement) {
      if (this.highlightTimer) {
        clearTimeout(this.highlightTimer);
        messageElement.classList.remove(
          'ring-2',
          'ring-n-amber-400',
          'ring-offset-2',
          'ring-offset-n-surface-1',
          'rounded-lg'
        );
      }
      messageElement.classList.add(
        'ring-2',
        'ring-n-amber-400',
        'ring-offset-2',
        'ring-offset-n-surface-1',
        'rounded-lg'
      );
      this.highlightTimer = setTimeout(() => {
        messageElement.classList.remove(
          'ring-2',
          'ring-n-amber-400',
          'ring-offset-2',
          'ring-offset-n-surface-1',
          'rounded-lg'
        );
      }, 2500);
    },
    async loadMessageFromServer(messageId) {
      const conversationId = this.currentChat.id;
      if (!conversationId || !messageId) return;
      try {
        await this.$store.dispatch('fetchMessagesAround', {
          conversationId,
          around: messageId,
        });
        this.$nextTick(() => {
          const messageElement = document.getElementById('message' + messageId);
          if (messageElement) {
            this.isProgrammaticScroll = true;
            messageElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            this.highlightMessage(messageElement);
          }
        });
      } catch (error) {
        // Ignore error
      }
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    toggleSelectionMode() {
      this.isSelectionMode = !this.isSelectionMode;
      if (!this.isSelectionMode) {
        this.selectedMessages = [];
      }
    },
    toggleMessageSelection(messageId) {
      const index = this.selectedMessages.indexOf(messageId);
      if (index > -1) {
        this.selectedMessages.splice(index, 1);
      } else {
        this.selectedMessages.push(messageId);
      }
    },
    isMessageSelected(messageId) {
      return this.selectedMessages.includes(messageId);
    },
    openForwardModal() {
      if (this.selectedMessages.length > 0) {
        this.isForwardModalOpen = true;
      }
    },
    closeForwardModal() {
      this.isForwardModalOpen = false;
    },
    async handleForwardMultiple(targetConversationId) {
      try {
        await this.$store.dispatch('forwardMultipleMessages', {
          messageIds: this.selectedMessages,
          targetConversationId,
        });
        this.$store.dispatch(
          'setAlert',
          this.$t('CONVERSATION.FORWARD_MODAL.SUCCESS_MULTIPLE', {
            count: this.selectedMessages.length,
          })
        );
        this.closeForwardModal();
        this.toggleSelectionMode();
      } catch (error) {
        this.$store.dispatch(
          'setAlert',
          this.$t('CONVERSATION.FORWARD_MODAL.ERROR')
        );
      }
    },
    toggleReplyEditorSize() {
      this.resizableEditorWrapperRef?.toggleEditorExpand?.();
    },
    resetReplyEditorHeight() {
      this.resizableEditorWrapperRef?.resetEditorHeight?.();
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewRef"
    class="flex flex-col justify-between flex-grow h-full min-w-0 m-0"
  >
    <div ref="topBannerRef">
      <Banner
        v-if="isInstagramRestrictionBannerVisible"
        color-scheme="warning"
        class="mx-2 mt-2 min-h-12 !h-auto rounded-lg"
        :banner-message="$t('CONVERSATION.INSTAGRAM_RESTRICTION_BANNER')"
        :href-link="instagramRestrictionStatusUrl"
        :href-link-text="$t('CONVERSATION.INSTAGRAM_RESTRICTION_STATUS_LINK')"
      />
      <Banner
        v-if="!currentChat.can_reply"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="replyWindowBannerMessage"
        :href-link="replyWindowLink"
        :href-link-text="replyWindowLinkText"
      />
      <Banner
        v-if="hasDuplicateInstagramInbox"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
      />
    </div>
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :class="{ 'waha-chat-bg': isWahaConversation }"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      :is-selection-mode="isSelectionMode"
      :selected-messages="selectedMessages"
      @retry="handleMessageRetry"
      @toggle-message-selection="toggleMessageSelection"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
          </li>
        </transition>
        <ReferralBubble v-if="referralData" :referral="referralData" />
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div
      class="flex relative flex-col"
      :class="isWahaConversation ? 'waha-reply' : 'bg-n-surface-1'"
    >
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-8 -top-8 overflow-visible"
      >
        <div class="flex gap-2 my-2.5 mx-auto">
          <div
            v-if="typingGroups.contacts.length"
            class="flex gap-1 items-center py-1 px-2 shadow-md rounded-full bg-n-teal-2 text-n-teal-9 text-xs font-medium"
          >
            <fluent-icon icon="edit" size="12" class="text-n-teal-9" />
            <span class="font-semibold">{{
              typingGroups.contacts[0].name
            }}</span>
            <span class="opacity-75">{{ $t('CHAT_LIST.TYPING') }}</span>
            <span class="flex gap-0.5 items-center">
              <span
                class="w-1 h-1 rounded-full animate-bounce bg-n-teal-9"
                :style="
                  typingGroups.contacts.length ? 'animation-delay: -0.3s' : ''
                "
              />
              <span
                class="w-1 h-1 rounded-full animate-bounce bg-n-teal-9"
                :style="
                  typingGroups.contacts.length ? 'animation-delay: -0.15s' : ''
                "
              />
              <span class="w-1 h-1 rounded-full animate-bounce bg-n-teal-9" />
            </span>
          </div>
          <div
            v-if="typingGroups.agents.length"
            class="flex gap-1 items-center py-1 px-2 shadow-md rounded-full bg-blue-50 text-blue-700 text-xs font-medium"
          >
            <fluent-icon icon="person" size="12" class="text-blue-700" />
            <span class="font-semibold">{{ typingGroups.agents[0].name }}</span>
            <span class="opacity-75">{{ $t('CHAT_LIST.TYPING') }}</span>
            <span class="flex gap-0.5 items-center">
              <span
                class="w-1 h-1 rounded-full animate-bounce"
                :style="
                  typingGroups.agents.length
                    ? 'background-color: #1d4ed8; animation-delay: -0.3s'
                    : ''
                "
              />
              <span
                class="w-1 h-1 rounded-full animate-bounce"
                :style="
                  typingGroups.agents.length
                    ? 'background-color: #1d4ed8; animation-delay: -0.15s'
                    : ''
                "
              />
              <span
                class="w-1 h-1 rounded-full animate-bounce"
                :style="
                  typingGroups.agents.length ? 'background-color: #1d4ed8' : ''
                "
              />
            </span>
          </div>
        </div>
      </div>
      <ResizableEditorWrapper
        ref="resizableEditorWrapperRef"
        :container-height="Math.max(0, containerHeight - topBannerHeight)"
      >
        <ReplyBox @toggle-editor-size="toggleReplyEditorSize" />
      </ResizableEditorWrapper>
    </div>

    <!-- Barra flotante de selección múltiple -->
    <transition name="slide-up">
      <div
        v-if="isSelectionMode"
        class="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 bg-n-solid-2 shadow-2xl rounded-full px-6 py-3 flex items-center gap-4 border border-n-strong"
      >
        <span class="text-n-slate-12 font-medium">
          {{
            $t('CONVERSATION.SELECTION_MODE.MESSAGES_SELECTED', {
              count: selectedMessages.length,
            })
          }}
        </span>
        <div class="flex gap-2">
          <NextButton
            variant="ghost"
            color="slate"
            size="sm"
            :label="$t('CONVERSATION.SELECTION_MODE.CANCEL')"
            @click="toggleSelectionMode"
          />
          <NextButton
            size="sm"
            icon="i-lucide-arrow-right"
            :label="
              $t('CONVERSATION.SELECTION_MODE.FORWARD_SELECTED', {
                count: selectedMessages.length,
              })
            "
            :disabled="selectedMessages.length === 0"
            @click="openForwardModal"
          />
        </div>
      </div>
    </transition>

    <!-- Modal de reenvío -->
    <ForwardMessageModal
      v-if="isForwardModalOpen"
      :message-count="selectedMessages.length"
      @close="closeForwardModal"
      @forward="handleForwardMultiple"
    />
  </div>
</template>

<style lang="scss">
$waha-doodle-shapes: "<g fill='none' stroke='%23fff' stroke-width='1.4' stroke-opacity='0.055' stroke-linecap='round' stroke-linejoin='round'><path d='M12 18h18a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-9l-5 5v-5h-4a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3z'/><path d='M84 21c-2-3-7-3-7 1.5 0-4.5-5-4.5-7-1.5-2.5 3.5 7 9.5 7 9.5s9.5-6 7-9.5z'/><path d='M98 55l2.2 4.4 4.9.7-3.5 3.4.8 4.9-4.4-2.3-4.4 2.3.8-4.9-3.5-3.4 4.9-.7z'/><path d='M22 95c-1-3 1-6 3-7l3 3c-1 2-1 3-2 4 2 3 5 6 8 8 1-1 2-1 4-2l3 3c-1 2-4 4-7 3-5-2-10-7-12-12z'/><path d='M70 85V71l10-2v14'/><circle cx='67' cy='85' r='3'/><circle cx='77' cy='83' r='3'/><circle cx='55' cy='18' r='5'/><path d='M55 8v3M55 25v3M45 18h3M62 18h3M48.5 11.5l2 2M60.5 23.5l2 2M61.5 11.5l-2 2M49.5 23.5l-2 2'/><path d='M18 55l2 4h-4l-2-4z M16 59l-3 5h8z'/><path d='M105 100c0 5-4 8-8 8-3 0-5-2-5-5 0-2 2-4 4-4l8-3v3z'/></g>";
$waha-doodle-shapes-light: "<g fill='none' stroke='%23000' stroke-width='1.4' stroke-opacity='0.05' stroke-linecap='round' stroke-linejoin='round'><path d='M12 18h18a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-9l-5 5v-5h-4a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3z'/><path d='M84 21c-2-3-7-3-7 1.5 0-4.5-5-4.5-7-1.5-2.5 3.5 7 9.5 7 9.5s9.5-6 7-9.5z'/><path d='M98 55l2.2 4.4 4.9.7-3.5 3.4.8 4.9-4.4-2.3-4.4 2.3.8-4.9-3.5-3.4 4.9-.7z'/><path d='M22 95c-1-3 1-6 3-7l3 3c-1 2-1 3-2 4 2 3 5 6 8 8 1-1 2-1 4-2l3 3c-1 2-4 4-7 3-5-2-10-7-12-12z'/><path d='M70 85V71l10-2v14'/><circle cx='67' cy='85' r='3'/><circle cx='77' cy='83' r='3'/><circle cx='55' cy='18' r='5'/><path d='M55 8v3M55 25v3M45 18h3M62 18h3M48.5 11.5l2 2M60.5 23.5l2 2M61.5 11.5l-2 2M49.5 23.5l-2 2'/><path d='M18 55l2 4h-4l-2-4z M16 59l-3 5h8z'/><path d='M105 100c0 5-4 8-8 8-3 0-5-2-5-5 0-2 2-4 4-4l8-3v3z'/></g>";

.conversation-panel.waha-chat-bg {
  background-color: #efeae2;
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>#{$waha-doodle-shapes-light}</svg>");
  background-size: 108px 108px;

  .left-bubble,
  .right-bubble {
    border-radius: 8px;
  }

  .left-bubble.bg-n-slate-4 {
    background-color: #ffffff;
    box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
  }

  .right-bubble.bg-n-solid-blue {
    background-color: #d9fdd3;
    box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
  }

  .message-bubble-container:not(.group-with-next) {
    .left-bubble.bg-n-slate-4,
    .right-bubble.bg-n-solid-blue {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        width: 8px;
        height: 13px;
        background-repeat: no-repeat;
      }
    }

    .left-bubble.bg-n-slate-4::after {
      left: -8px;
      background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13' width='8' height='13'><path d='M2.812 1H8v11.193L1.533 2.568C.474 2.156 1.042 1 2.812 1z' fill='%23ffffff'/></svg>");
    }

    .right-bubble.bg-n-solid-blue::after {
      right: -8px;
      background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13' width='8' height='13'><path d='M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z' fill='%23d9fdd3'/></svg>");
    }
  }
}

// WhatsApp-style composer bar — only inside WAHA conversations
.waha-reply {
  background-color: #efeae2;
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>#{$waha-doodle-shapes-light}</svg>");
  background-size: 108px 108px;

  // Remove the gradient drag-handle strip so the wallpaper continues seamlessly
  .resizable-editor-wrapper > .group {
    background: none;
    backdrop-filter: none;
  }

  .reply-box.reply-box {
    border: none;
    border-radius: 24px;
    background-color: #ffffff;
    box-shadow: 0 1px 3px rgba(11, 20, 26, 0.08);
    margin: 6px 8px 8px;

    &.is-private {
      background-color: #fef3c7;
    }

    // Slimmer composer, closer to WhatsApp Web's single bar
    > div:first-child {
      height: 2.5rem;
    }

    > div:has(> .left-wrap) {
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
    }

    .reply-box__top {
      min-height: 0;
    }
  }
}

body.dark .waha-reply {
  background-color: #0b141a;
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>#{$waha-doodle-shapes}</svg>");
  background-size: 108px 108px;

  .reply-box.reply-box {
    background-color: #2a3942;
    box-shadow: 0 1px 3px rgba(11, 20, 26, 0.4);

    &.is-private {
      background-color: #3b3416;
    }
  }
}

body.dark .conversation-panel.waha-chat-bg {
  background-color: #0b141a;
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>#{$waha-doodle-shapes}</svg>");
  background-size: 108px 108px;

  .left-bubble.bg-n-slate-4 {
    background-color: #202c33;
    box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.3);
  }

  .right-bubble.bg-n-solid-blue {
    background-color: #005c4b;
    box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.3);
  }

  .message-bubble-container:not(.group-with-next) {
    .left-bubble.bg-n-slate-4::after {
      background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13' width='8' height='13'><path d='M2.812 1H8v11.193L1.533 2.568C.474 2.156 1.042 1 2.812 1z' fill='%23202c33'/></svg>");
    }

    .right-bubble.bg-n-solid-blue::after {
      background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13' width='8' height='13'><path d='M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z' fill='%23005c4b'/></svg>");
    }
  }
}
</style>
